import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AppShell } from "@/components/AppShell";
import { Spinner } from "@/components/Spinner";
import { api, apiError, FRAMEWORK_LABELS } from "@/lib/api";

const SEVERITIES = ["critical", "high", "medium", "low"];
const REVIEW_STATUSES = ["requested", "in_review", "completed", "cancelled"];
const LEAD_STATUSES = ["new", "contacted", "qualified", "closed"];

export default function Admin() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [users, setUsers] = useState([]);
  const [purchases, setPurchases] = useState({ purchases: [], payments: [] });
  const [reviews, setReviews] = useState([]);
  const [leads, setLeads] = useState([]);
  const [logs, setLogs] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [billing, setBilling] = useState(null);
  const [editing, setEditing] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetWord, setResetWord] = useState("");
  const [resetting, setResetting] = useState(false);

  const runReset = async () => {
    setResetting(true);
    try {
      const { data } = await api.post("/admin/maintenance/reset-demo-data", { confirm: resetWord });
      toast.success(`Demo data cleared. ${data.removed.users} accounts and ${data.removed.assessments} assessments removed.`);
      setResetOpen(false);
      setResetWord("");
      await loadAll();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setResetting(false);
    }
  };

  const loadAll = async () => {
    // Fire independently so the page paints as soon as the overview lands instead of
    // waiting for the largest payload (the question bank).
    const get = (url, apply) =>
      api
        .get(url)
        .then(({ data }) => apply(data))
        .catch((e) => toast.error(apiError(e)));
    await Promise.all([
      get("/admin/overview", setOverview),
      get("/admin/billing/settings", setBilling),
      get("/admin/funnel", setFunnel),
      get("/admin/users", (d) => setUsers(d.users)),
      get("/admin/assessments", (d) => setAssessments(d.assessments)),
      get("/admin/purchases", setPurchases),
      get("/admin/expert-reviews", (d) => setReviews(d.requests)),
      get("/admin/leads", (d) => setLeads(d.leads)),
      get("/admin/audit-logs", (d) => setLogs(d.logs)),
      get("/admin/questions", (d) => setQuestions(d.questions)),
    ]);
  };

  useEffect(() => {
    loadAll();
  }, []);

  const saveQuestion = async () => {
    try {
      const { data } = await api.put(`/admin/questions/${editing.code}`, {
        text: editing.text,
        explanation: editing.explanation,
        requirement_category: editing.requirement_category,
        expected_evidence: editing.expected_evidence,
        remediation_guidance: editing.remediation_guidance,
        severity_base: editing.severity_base,
        frameworks: editing.frameworks,
      });
      toast.success(`Saved as version ${data.version}`);
      setEditing(null);
      loadAll();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const toggleActive = async (q) => {
    try {
      await api.put(`/admin/questions/${q.code}`, { active: !q.active });
      toast.success(`${q.code} ${q.active ? "deactivated" : "activated"}`);
      loadAll();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!overview)
    return (
      <AppShell breadcrumb="admin">
        <Spinner label="Loading admin data" />
      </AppShell>
    );

  return (
    <AppShell breadcrumb="admin">
      <p className="overline text-navy-800">Administration</p>
      <h1 className="mt-2 font-display text-3xl font-extrabold text-navy-900">Platform control</h1>

      <div className="mt-7 grid gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="admin-overview">
        {[
          ["Users", overview.users],
          ["Products", overview.products],
          ["Completed", overview.completed_assessments],
          ["Purchases", overview.purchases],
          ["Revenue", `$${(overview.revenue_cents / 100).toFixed(0)}`],
          ["Leads", overview.leads],
        ].map(([label, value]) => (
          <div key={label} className="border border-slate-200 bg-white p-4">
            <p className="overline text-slate-500">{label}</p>
            <p className="mt-1.5 font-display text-2xl font-extrabold text-navy-900">{value}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="questions" className="mt-8">
        <TabsList
          className="flex w-full max-w-full justify-start gap-1 overflow-x-auto whitespace-nowrap sm:flex-wrap sm:justify-center"
          data-testid="admin-tabs"
        >
          {["questions", "billing", "funnel", "users", "assessments", "purchases", "reviews", "leads", "audit"].map((t) => (
            <TabsTrigger key={t} value={t} data-testid={`admin-tab-${t}`} className="shrink-0 capitalize">
              {t}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="questions" className="mt-6">
          <div className="overflow-x-auto border border-slate-200 bg-white">
            <Table data-testid="admin-questions-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Question</TableHead>
                  <TableHead>Frameworks</TableHead>
                  <TableHead>Severity</TableHead>
                  <TableHead>Ver</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {questions.map((q) => (
                  <TableRow key={q.code} data-testid={`admin-question-row-${q.code}`}>
                    <TableCell className="font-mono text-xs">{q.code}</TableCell>
                    <TableCell className="max-w-md text-xs">{q.text}</TableCell>
                    <TableCell className="text-[10px] font-mono">
                      {q.frameworks.map((f) => FRAMEWORK_LABELS[f]).join(", ")}
                    </TableCell>
                    <TableCell className="text-xs capitalize">{q.severity_base}</TableCell>
                    <TableCell className="font-mono text-xs">v{q.version}</TableCell>
                    <TableCell>
                      <Switch
                        checked={q.active}
                        onCheckedChange={() => toggleActive(q)}
                        data-testid={`admin-question-active-${q.code}`}
                      />
                    </TableCell>
                    <TableCell className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => setEditing({ ...q })} data-testid={`admin-edit-question-${q.code}`}>
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={async () => {
                          try {
                            const { data } = await api.post(`/admin/questions/${q.code}/restore-default`);
                            toast.success(`${q.code} restored to seeded wording (v${data.version})`);
                            loadAll();
                          } catch (e) {
                            toast.error(apiError(e));
                          }
                        }}
                        data-testid={`admin-restore-question-${q.code}`}
                      >
                        Restore default
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <p className="mt-3 text-xs text-slate-500">
            Editing wording, evidence, remediation or severity creates a new question version. Published
            versions are never overwritten and remain attached to historical assessments.
          </p>
        </TabsContent>

        <TabsContent value="billing" className="mt-6">
          <div className="space-y-4" data-testid="admin-billing">
            <div className="border border-rose-200 bg-rose-50/60 p-6" data-testid="admin-danger-zone">
              <p className="overline text-rose-700">Danger zone</p>
              <h2 className="mt-2 font-display text-lg font-bold text-navy-900">Clear demo data</h2>
              <p className="mt-2 max-w-xl text-sm leading-relaxed text-slate-600">
                Deletes every non-admin account and all products, assessments, answers, results,
                purchases, payments, subscriptions, reviews, leads, funnel events and audit logs.
                Admin logins and the question bank are kept. This cannot be undone.
              </p>
              <Button
                variant="outline"
                className="mt-5 border-rose-300 text-rose-700 hover:bg-rose-100"
                onClick={() => setResetOpen(true)}
                data-testid="open-reset-demo-data"
              >
                Clear demo data
              </Button>
            </div>

            <div className="border border-slate-200 bg-white p-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="max-w-xl">
                  <p className="overline text-navy-800">Report access</p>
                  <h2 className="mt-2 font-display text-lg font-bold text-navy-900">
                    Let every account generate paid reports
                  </h2>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    When this is on, any signed-in user can generate the full report without paying.
                    When it is off, a user needs an active Founding Plan subscription (${billing?.subscription_price_usd}/month)
                    or an individual grant below.
                  </p>
                </div>
                <Switch
                  checked={!!billing?.settings?.free_reports_enabled}
                  onCheckedChange={async (v) => {
                    try {
                      await api.put("/admin/billing/settings", { free_reports_enabled: v });
                      toast.success(v ? "Report access opened to all users" : "Payment now required");
                      loadAll();
                    } catch (e) {
                      toast.error(apiError(e));
                    }
                  }}
                  data-testid="admin-free-reports-toggle"
                />
              </div>
              <p className="mt-4 font-mono text-[11px] text-slate-500">
                Paddle {billing?.paddle_configured ? "configured" : "not configured: add PADDLE_API_KEY and price IDs to enable checkout"}
                {" · "}expert review ${billing?.expert_review_price_usd} one-time
              </p>
            </div>

            <div className="border border-slate-200 bg-white p-6">
              <p className="overline text-navy-800">Per-user report access</p>
              <div className="mt-4 space-y-2" data-testid="admin-user-grants">
                {users.map((u) => (
                  <div key={u.user_id} className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-2">
                    <div>
                      <p className="text-sm text-navy-900">{u.email}</p>
                      <p className="font-mono text-[10px] text-slate-500">
                        {u.user_id} · {u.role}
                        {u.report_access_granted ? " · access granted" : ""}
                      </p>
                    </div>
                    <Switch
                      checked={!!u.report_access_granted}
                      onCheckedChange={async (v) => {
                        try {
                          await api.put(`/admin/users/${u.user_id}/report-access`, { granted: v });
                          toast.success(v ? "Report access granted" : "Report access revoked");
                          loadAll();
                        } catch (e) {
                          toast.error(apiError(e));
                        }
                      }}
                      data-testid={`admin-grant-access-${u.user_id}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <p className="overline text-navy-800">Paddle subscriptions</p>
              <SimpleTable
                testId="admin-subscriptions-table"
                columns={["subscription_id", "user_id", "status", "customer_id", "updated_at"]}
                rows={billing?.subscriptions || []}
              />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="funnel" className="mt-6">
          {!funnel ? (
            <Spinner label="Loading funnel" />
          ) : (
            <div className="border border-slate-200 bg-white p-6" data-testid="admin-funnel">
            <p className="overline text-navy-800">Conversion funnel</p>
            <p className="mt-2 text-sm text-slate-600">
              Signup → purchase conversion:{" "}
              <span className="font-display font-bold text-navy-900">{funnel.signup_to_purchase_conversion_pct}%</span>
            </p>
            <div className="mt-6 space-y-2">
              {funnel.steps.map((s) => {
                const max = Math.max(...funnel.steps.map((x) => x.count), 1);
                return (
                  <div key={s.event}>
                    <div className="flex justify-between text-xs text-slate-600">
                      <span className="font-mono">{s.event}</span>
                      <span className="font-semibold">{s.count}</span>
                    </div>
                    <div className="mt-1 h-2 w-full bg-slate-100">
                      <div className="h-2 bg-navy-800" style={{ width: `${(s.count / max) * 100}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          )}
        </TabsContent>

        <TabsContent value="users" className="mt-6">
          <SimpleTable
            testId="admin-users-table"
            columns={["email", "name", "role", "auth_provider", "created_at"]}
            rows={users}
          />
        </TabsContent>

        <TabsContent value="assessments" className="mt-6">
          <div className="overflow-x-auto border border-slate-200 bg-white">
            <Table data-testid="admin-assessments-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Account</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Answered</TableHead>
                  <TableHead>Report</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {assessments.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-sm text-slate-600">
                      No assessments yet.
                    </TableCell>
                  </TableRow>
                )}
                {assessments.map((a) => (
                  <TableRow key={a.assessment_id} data-testid={`admin-assessment-row-${a.assessment_id}`}>
                    <TableCell className="text-xs">{a.user_email}</TableCell>
                    <TableCell className="text-xs">{a.product_name}</TableCell>
                    <TableCell className="text-xs capitalize">{String(a.status).replace(/_/g, " ")}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {a.answered}/{a.total}
                    </TableCell>
                    <TableCell className="text-xs">{a.purchased ? "Purchased" : "Not purchased"}</TableCell>
                    <TableCell className="font-mono text-[11px] text-slate-500">
                      {new Date(a.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/admin/report/${a.assessment_id}`)}
                        data-testid={`admin-view-report-${a.assessment_id}`}
                      >
                        View report
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="purchases" className="mt-6">
          <SimpleTable
            testId="admin-purchases-table"
            columns={["purchase_id", "assessment_id", "amount_cents", "currency", "status", "purchased_at"]}
            rows={purchases.purchases}
          />
          <p className="mt-6 overline text-navy-800">Payments</p>
          <SimpleTable
            testId="admin-payments-table"
            columns={["payment_id", "provider", "amount_cents", "status", "mocked", "created_at"]}
            rows={purchases.payments}
          />
        </TabsContent>

        <TabsContent value="reviews" className="mt-6">
          <div className="space-y-3" data-testid="admin-reviews-list">
            {reviews.length === 0 && <p className="text-sm text-slate-600">No expert review requests yet.</p>}
            {reviews.map((r) => (
              <div key={r.request_id} className="border border-slate-200 bg-white p-5" data-testid={`admin-review-${r.request_id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-display text-sm font-bold text-navy-900">{r.product_name}</p>
                    <p className="font-mono text-[11px] text-slate-500">
                      {r.request_id} · {r.user_email} · ${r.price_cents / 100} · payment {r.payment_status}
                    </p>
                  </div>
                  <Select
                    value={r.status}
                    onValueChange={async (v) => {
                      try {
                        await api.put(`/admin/expert-reviews/${r.request_id}`, { status: v });
                        toast.success("Status updated");
                        loadAll();
                      } catch (e) {
                        toast.error(apiError(e));
                      }
                    }}
                  >
                    <SelectTrigger className="w-44" data-testid={`admin-review-status-${r.request_id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {REVIEW_STATUSES.map((s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {r.notes && <p className="mt-3 text-xs text-slate-600">Customer notes: {r.notes}</p>}
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="leads" className="mt-6">
          <div className="space-y-3" data-testid="admin-leads-list">
            {leads.length === 0 && <p className="text-sm text-slate-600">No consulting leads yet.</p>}
            {leads.map((l) => (
              <div key={l.lead_id} className="border border-slate-200 bg-white p-5" data-testid={`admin-lead-${l.lead_id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-display text-sm font-bold text-navy-900">
                      {l.name} · {l.company}
                    </p>
                    <p className="font-mono text-[11px] text-slate-500">
                      {l.email} · {l.interest}
                    </p>
                  </div>
                  <Select
                    value={l.status}
                    onValueChange={async (v) => {
                      try {
                        await api.put(`/admin/leads/${l.lead_id}`, { status: v });
                        toast.success("Lead updated");
                        loadAll();
                      } catch (e) {
                        toast.error(apiError(e));
                      }
                    }}
                  >
                    <SelectTrigger className="w-40" data-testid={`admin-lead-status-${l.lead_id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LEAD_STATUSES.map((s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {l.message && <p className="mt-3 text-xs text-slate-600">{l.message}</p>}
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="audit" className="mt-6">
          <SimpleTable
            testId="admin-audit-table"
            columns={["at", "admin_email", "action", "entity", "entity_id"]}
            rows={logs}
          />
        </TabsContent>
      </Tabs>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="admin-question-dialog">
          <DialogHeader>
            <DialogTitle>Edit {editing?.code} (creates a new version)</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs">Question text</Label>
                <Textarea
                  rows={2}
                  value={editing.text}
                  onChange={(e) => setEditing({ ...editing, text: e.target.value })}
                  data-testid="admin-question-text-input"
                />
              </div>
              <div>
                <Label className="text-xs">Explanation</Label>
                <Textarea
                  rows={2}
                  value={editing.explanation}
                  onChange={(e) => setEditing({ ...editing, explanation: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-xs">Requirement category</Label>
                <Input
                  value={editing.requirement_category}
                  onChange={(e) => setEditing({ ...editing, requirement_category: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-xs">Expected evidence</Label>
                <Textarea
                  rows={2}
                  value={editing.expected_evidence}
                  onChange={(e) => setEditing({ ...editing, expected_evidence: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-xs">Remediation guidance</Label>
                <Textarea
                  rows={2}
                  value={editing.remediation_guidance}
                  onChange={(e) => setEditing({ ...editing, remediation_guidance: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-xs">Severity</Label>
                <Select
                  value={editing.severity_base}
                  onValueChange={(v) => setEditing({ ...editing, severity_base: v })}
                >
                  <SelectTrigger data-testid="admin-question-severity-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SEVERITIES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Framework mappings</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {Object.keys(FRAMEWORK_LABELS).map((f) => {
                    const on = editing.frameworks.includes(f);
                    return (
                      <button
                        key={f}
                        type="button"
                        onClick={() =>
                          setEditing({
                            ...editing,
                            frameworks: on
                              ? editing.frameworks.filter((x) => x !== f)
                              : [...editing.frameworks, f],
                          })
                        }
                        className={`border px-3 py-1.5 font-mono text-[11px] ${
                          on ? "border-navy-800 bg-navy-900 text-white" : "border-slate-200 text-slate-600"
                        }`}
                        data-testid={`admin-question-framework-${f}`}
                      >
                        {FRAMEWORK_LABELS[f]}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button className="bg-navy-800 text-white hover:bg-navy-700" onClick={saveQuestion} data-testid="admin-save-question-button">
              Save new version
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={resetOpen} onOpenChange={(o) => { setResetOpen(o); if (!o) setResetWord(""); }}>
        <DialogContent data-testid="reset-demo-data-dialog">
          <DialogHeader>
            <DialogTitle>Clear demo data</DialogTitle>
          </DialogHeader>
          <p className="text-sm leading-relaxed text-slate-600">
            Every non-admin account and all products, assessments, answers, results, purchases,
            payments, subscriptions, reviews, leads, funnel events and audit logs will be deleted.
            Admin logins and the question bank stay. This cannot be undone.
          </p>
          <div className="mt-4">
            <Label htmlFor="reset-word">Type RESET to confirm</Label>
            <Input
              id="reset-word"
              value={resetWord}
              onChange={(e) => setResetWord(e.target.value)}
              placeholder="RESET"
              className="mt-2"
              data-testid="reset-confirm-input"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setResetOpen(false)} data-testid="cancel-reset">
              Cancel
            </Button>
            <Button
              onClick={runReset}
              disabled={resetting || resetWord !== "RESET"}
              className="bg-rose-700 text-white hover:bg-rose-800"
              data-testid="confirm-reset-demo-data"
            >
              {resetting ? "Clearing…" : "Clear demo data"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

const SimpleTable = ({ columns, rows, testId }) => (
  <div className="overflow-x-auto border border-slate-200 bg-white">
    <Table data-testid={testId}>
      <TableHeader>
        <TableRow>
          {columns.map((c) => (
            <TableHead key={c} className="text-xs">
              {c.replace(/_/g, " ")}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.length === 0 && (
          <TableRow>
            <TableCell colSpan={columns.length} className="text-xs text-slate-500">
              No records.
            </TableCell>
          </TableRow>
        )}
        {rows.map((r, i) => (
          <TableRow key={i}>
            {columns.map((c) => (
              <TableCell key={c} className="max-w-xs truncate font-mono text-[11px]">
                {String(r[c] ?? "-")}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);
